import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type DocumentTemplatesResult } from '@whinerp/client';
import { BlockEditor, Button, ErrorState, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { BLOCK_PALETTES, type Block, type BlockDocument } from '@whinerp/contracts/blocks';
import { useT, useErrorMessage } from '../../app/i18n.js';
import { blockEditorLabels } from '../marketing/block-labels.js';

/**
 * Document templates (F-050).
 *
 * The block editor with the document palette - the same component an email, a
 * landing page and a knowledge article use, so there is nothing new to learn
 * and no second editor to keep in step.
 *
 * **The field picker offers exactly what the server accepts.** The list comes
 * from the same response as the templates, per object. A picker offering a
 * field the save refuses is a control that fails after somebody has used it.
 */

type Template = DocumentTemplatesResult['templates'][number];

const EMPTY: BlockDocument = { blocks: [] };

/** Appends a token to the last block that holds words, or adds a text block for it. */
function insertToken(document: BlockDocument, token: string): BlockDocument {
  const blocks = [...document.blocks];
  for (let index = blocks.length - 1; index >= 0; index -= 1) {
    const block = blocks[index];
    if (block && (block.type === 'text' || block.type === 'heading' || block.type === 'quote')) {
      blocks[index] = { ...block, text: `${block.text ?? ''}{{${token}}}` };
      return { blocks };
    }
  }
  const added: Block = { id: `b${Math.random().toString(36).slice(2, 9)}`, type: 'text', text: `{{${token}}}` };
  return { blocks: [...blocks, added] };
}

export function DocumentTemplatesPanel(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Template | 'new' | null>(null);
  const [name, setName] = useState('');
  const [objectKey, setObjectKey] = useState<'quote' | 'deal'>('quote');
  const [format, setFormat] = useState<'pdf' | 'docx'>('pdf');
  const [document, setDocument] = useState<BlockDocument>(EMPTY);

  const templates = useQuery<DocumentTemplatesResult, ApiError>({
    queryKey: ['document-templates'],
    queryFn: () => api.documentTemplates({}),
  });

  const save = useMutation<unknown, ApiError>({
    mutationFn: () =>
      api.saveDocumentTemplate({
        body: {
          templateId: editing && editing !== 'new' ? editing.templateId : null,
          name,
          objectKey,
          format,
          blocks: document.blocks,
        },
      }),
    onSuccess: async () => {
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: ['document-templates'] });
    },
  });

  if (templates.isPending) return <Skeleton className="h-block-md w-full" />;
  if (templates.isError) {
    return (
      <ErrorState
        title={t('state.error')}
        body={errorText(templates.error)}
        onRetry={() => void templates.refetch()}
      />
    );
  }

  const open = (template: Template | 'new'): void => {
    save.reset();
    setEditing(template);
    setName(template === 'new' ? '' : template.name);
    setObjectKey(template === 'new' ? 'quote' : template.objectKey);
    setFormat(template === 'new' ? 'pdf' : template.format);
    setDocument(template === 'new' ? EMPTY : { blocks: template.blocks as Block[] });
  };

  const fields = templates.data.fields[objectKey];

  return (
    <Panel data-region="document-templates" id="document-templates">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <SectionLabel>{t('doc.title')}</SectionLabel>
            <p className="text-meta text-ink-meta">{t('doc.body')}</p>
          </div>
          {editing === null ? (
            <Button intent="secondary" size="small" data-doc-new onClick={() => open('new')}>
              {t('doc.new')}
            </Button>
          ) : null}
        </div>

        {editing === null ? (
          templates.data.templates.length === 0 ? (
            <p data-doc-none className="text-meta text-ink-meta">
              {t('doc.none')}
            </p>
          ) : (
            <ul className="flex flex-col">
              {templates.data.templates.map((template) => (
                <li
                  key={template.templateId}
                  data-doc-template={template.templateId}
                  className="flex flex-wrap items-center gap-2 border-t border-t-rule py-1 first:border-t-0"
                >
                  <span className="min-w-0 flex-1 truncate text-body text-ink">{template.name}</span>
                  <span className="text-meta text-ink-meta">
                    {t(`doc.object.${template.objectKey}` as 'doc.object.quote')} ·{' '}
                    {template.format.toUpperCase()}
                  </span>
                  <Button
                    intent="ghost"
                    size="small"
                    data-doc-edit={template.templateId}
                    onClick={() => open(template)}
                  >
                    {t('doc.edit')}
                  </Button>
                </li>
              ))}
            </ul>
          )
        ) : (
          <div data-doc-editor className="flex flex-col gap-3">
            {save.error ? (
              <p
                data-doc-refusal
                className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text"
              >
                {errorText(save.error)}
              </p>
            ) : null}

            <div className="flex flex-wrap items-end gap-2">
              <label className="flex min-w-0 flex-1 flex-col gap-1 text-dense text-ink-600">
                {t('doc.name')}
                <input
                  data-doc-name
                  className="h-input rounded-control border border-control bg-surface px-3 text-body text-ink"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </label>
              <label className="flex flex-col gap-1 text-dense text-ink-600">
                {t('doc.object')}
                <select
                  data-doc-object
                  className="h-input rounded-control border border-control bg-surface px-2 text-body text-ink"
                  value={objectKey}
                  onChange={(event) => setObjectKey(event.target.value as 'quote' | 'deal')}
                >
                  <option value="quote">{t('doc.object.quote')}</option>
                  <option value="deal">{t('doc.object.deal')}</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-dense text-ink-600">
                {t('doc.format')}
                <select
                  data-doc-format
                  className="h-input rounded-control border border-control bg-surface px-2 text-body text-ink"
                  value={format}
                  onChange={(event) => setFormat(event.target.value as 'pdf' | 'docx')}
                >
                  <option value="pdf">PDF</option>
                  <option value="docx">DOCX</option>
                </select>
              </label>
            </div>
            {/* Said where the choice is made, not discovered when a quote with
                an Arabic name is refused. */}
            <p className="text-meta text-ink-meta">{t('doc.formatNote')}</p>

            <div className="flex flex-col gap-1">
              <SectionLabel>{t('doc.fields')}</SectionLabel>
              <div className="flex flex-wrap gap-1">
                {fields.map((field) => (
                  <button
                    key={field.token}
                    type="button"
                    data-doc-field={field.token}
                    className="rounded-chip border border-rule bg-surface px-2 py-0.5 text-meta text-ink-600 hover:bg-sunken"
                    onClick={() => setDocument((current) => insertToken(current, field.token))}
                  >
                    {t(field.labelKey as 'doc.field.today')}
                  </button>
                ))}
              </div>
            </div>

            <BlockEditor
              data-doc-blocks
              palette={BLOCK_PALETTES.document}
              value={document}
              onChange={setDocument}
              labels={blockEditorLabels(t)}
            />

            <div className="flex flex-wrap gap-2">
              <Button intent="primary" size="small" data-doc-save onClick={() => save.mutate()}>
                {t('doc.save')}
              </Button>
              <Button intent="ghost" size="small" data-doc-cancel onClick={() => setEditing(null)}>
                {t('doc.cancel')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
}
